import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Home as HomeIcon, Building2, HardHat, Star, CheckCircle2, Shield, Clock, Award } from 'lucide-react';
import { Button } from '../components/ui/button';
import { QuoteForm } from '../components/QuoteForm';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HomePage = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Seed data first
        await axios.post(`${API}/seed`);
        
        // Then fetch
        const [testimonialsRes, projectsRes] = await Promise.all([
          axios.get(`${API}/testimonials`),
          axios.get(`${API}/projects`)
        ]);
        setTestimonials(testimonialsRes.data);
        setProjects(projectsRes.data.slice(0, 3));
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, []);

  const services = [
    {
      icon: HomeIcon,
      title: 'Residential Cleaning',
      description: 'From cozy apartments to luxury estates. Weekly, bi-weekly, or one-time deep cleaning services.',
      link: '/services'
    },
    {
      icon: Building2,
      title: 'Commercial Cleaning',
      description: 'Professional cleaning for offices, retail spaces, and commercial properties of all sizes.',
      link: '/services'
    },
    {
      icon: HardHat,
      title: 'Post-Construction',
      description: 'Large-scale cleaning for hotels, apartments, and developments. 200+ rooms? No problem.',
      link: '/services'
    }
  ];

  const stats = [
    { value: '500+', label: 'Projects Completed' },
    { value: '25+', label: 'Years Experience' },
    { value: '100%', label: 'Satisfaction Rate' },
    { value: '24hr', label: 'Response Time' }
  ];

  const features = [
    { icon: Shield, text: 'Fully Insured & Bonded' },
    { icon: Clock, text: 'Flexible Scheduling' },
    { icon: Award, text: 'Quality Guaranteed' },
    { icon: CheckCircle2, text: 'Eco-Friendly Products' }
  ];

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center bg-brand-surface overflow-hidden" data-testid="hero-section">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-0 right-0 w-1/2 h-full">
            <img 
              src="https://images.unsplash.com/photo-1760072513442-9872656c1b07?w=1200&q=80" 
              alt="Modern living room"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-l from-transparent via-slate-50/80 to-slate-50"></div>
          </div>
        </div>

        <div className="relative max-w-7xl mx-auto px-5 md:px-8 lg:px-12 py-20 md:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left - Content */}
            <div className="stagger-children">
              <p className="text-brand-accent font-body text-sm font-semibold uppercase tracking-widest mb-4">
                Professional Cleaning Services
              </p>
              <h1 className="font-heading font-bold text-4xl sm:text-5xl lg:text-6xl text-brand-navy tracking-tight leading-tight mb-6">
                Pristine Spaces,<br />
                <span className="text-brand-slate">Professional Results</span>
              </h1>
              <p className="font-body text-lg text-brand-charcoal leading-relaxed mb-8 max-w-xl">
                From luxury homes to large-scale construction sites, ACH Cleaning delivers meticulous cleaning services across the Greater Austin Area.
              </p>

              {/* Features */}
              <div className="grid grid-cols-2 gap-4 mb-8">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-brand-navy/10 flex items-center justify-center">
                      <feature.icon className="w-4 h-4 text-brand-navy" />
                    </div>
                    <span className="font-body text-sm text-brand-charcoal">{feature.text}</span>
                  </div>
                ))}
              </div>

              <div className="flex flex-wrap gap-4">
                <Link to="/services">
                  <Button 
                    variant="outline" 
                    className="border-brand-navy text-brand-navy hover:bg-brand-navy hover:text-white font-body font-medium rounded-full px-6"
                    data-testid="explore-services-btn"
                  >
                    Explore Services
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
                <Link to="/about">
                  <Button 
                    variant="ghost" 
                    className="text-brand-charcoal hover:text-brand-navy font-body font-medium"
                    data-testid="learn-more-btn"
                  >
                    Learn About Us
                  </Button>
                </Link>
              </div>
            </div>

            {/* Right - Quote Form */}
            <div id="quote-form" className="animate-slide-up" style={{ animationDelay: '0.3s' }}>
              <QuoteForm />
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="bg-brand-navy py-12" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-5 md:px-8 lg:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center stats-item">
                <p className="font-heading font-bold text-3xl md:text-4xl text-white mb-1">{stat.value}</p>
                <p className="font-body text-sm text-slate-400">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="section-padding bg-white" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-5 md:px-8 lg:px-12">
          <div className="text-center mb-16">
            <p className="text-brand-accent font-body text-sm font-semibold uppercase tracking-widest mb-3">
              What We Offer
            </p>
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-brand-navy tracking-tight mb-4">
              Cleaning Services for Every Need
            </h2>
            <p className="font-body text-brand-charcoal max-w-2xl mx-auto">
              Whether you need regular home maintenance or large-scale post-construction cleanup, we have the expertise and capacity to deliver exceptional results.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <Link 
                key={index} 
                to={service.link}
                className="group"
                data-testid={`service-card-${index}`}
              >
                <div className="bg-brand-surface rounded-2xl p-8 h-full card-hover border border-transparent hover:border-slate-200">
                  <div className="w-14 h-14 rounded-xl bg-brand-navy/10 flex items-center justify-center mb-6 group-hover:bg-brand-navy transition-colors duration-300">
                    <service.icon className="w-7 h-7 text-brand-navy group-hover:text-white transition-colors duration-300" />
                  </div>
                  <h3 className="font-heading font-semibold text-xl text-brand-navy mb-3">
                    {service.title}
                  </h3>
                  <p className="font-body text-brand-charcoal leading-relaxed mb-4">
                    {service.description}
                  </p>
                  <span className="inline-flex items-center text-brand-navy font-body font-medium text-sm group-hover:gap-2 transition-all">
                    Learn More <ArrowRight className="w-4 h-4 ml-1" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Projects */}
      {projects.length > 0 && (
        <section className="section-padding bg-brand-surface" data-testid="featured-projects-section">
          <div className="max-w-7xl mx-auto px-5 md:px-8 lg:px-12">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-12">
              <div>
                <p className="text-brand-accent font-body text-sm font-semibold uppercase tracking-widest mb-3">
                  Our Work
                </p>
                <h2 className="font-heading font-bold text-3xl md:text-4xl text-brand-navy tracking-tight">
                  Featured Projects
                </h2>
              </div>
              <Link to="/projects">
                <Button 
                  variant="outline" 
                  className="border-brand-navy text-brand-navy hover:bg-brand-navy hover:text-white font-body font-medium rounded-full"
                  data-testid="view-all-projects-btn"
                >
                  View All Projects
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {projects.map((project, index) => (
                <div 
                  key={project.id} 
                  className="group relative overflow-hidden rounded-2xl aspect-[4/3] cursor-pointer"
                  data-testid={`featured-project-${index}`}
                >
                  <img 
                    src={project.image_url} 
                    alt={project.title}
                    className="w-full h-full object-cover img-zoom"
                  />
                  <div className="absolute inset-0 project-overlay opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-6">
                    <span className="text-slate-300 font-body text-xs uppercase tracking-wider mb-1">
                      {project.service_type}
                    </span>
                    <h4 className="font-heading font-semibold text-lg text-white">
                      {project.title}
                    </h4>
                    <p className="text-slate-300 font-body text-sm">
                      {project.location}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Testimonials Section */}
      {testimonials.length > 0 && (
        <section className="section-padding bg-white overflow-hidden" data-testid="testimonials-section">
          <div className="max-w-7xl mx-auto px-5 md:px-8 lg:px-12">
            <div className="text-center mb-12">
              <p className="text-brand-accent font-body text-sm font-semibold uppercase tracking-widest mb-3">
                Client Feedback
              </p>
              <h2 className="font-heading font-bold text-3xl md:text-4xl text-brand-navy tracking-tight">
                What Our Clients Say
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {testimonials.slice(0, 3).map((testimonial, index) => (
                <div 
                  key={testimonial.id} 
                  className="testimonial-card rounded-2xl p-6 border border-slate-100 card-hover"
                  data-testid={`testimonial-${index}`}
                >
                  <div className="flex gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
                    ))}
                  </div>
                  <p className="font-body text-brand-charcoal leading-relaxed mb-6">
                    "{testimonial.text}"
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-brand-navy flex items-center justify-center">
                      <span className="font-heading font-bold text-white text-sm">
                        {testimonial.name.charAt(0)}
                      </span>
                    </div>
                    <div>
                      <p className="font-body font-semibold text-brand-navy text-sm">
                        {testimonial.name}
                      </p>
                      <p className="font-body text-brand-accent text-xs">
                        {testimonial.location}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="section-padding bg-brand-navy" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-5 md:px-8 lg:px-12 text-center">
          <h2 className="font-heading font-bold text-3xl md:text-4xl text-white tracking-tight mb-4">
            Ready for a Spotless Space?
          </h2>
          <p className="font-body text-slate-300 text-lg mb-8 max-w-2xl mx-auto">
            Get your free quote today and experience the ACH difference. Professional cleaning services tailored to your needs.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button 
              onClick={() => document.getElementById('quote-form')?.scrollIntoView({ behavior: 'smooth' })}
              className="bg-white text-brand-navy hover:bg-slate-100 font-body font-medium rounded-full px-8 py-6 btn-shine"
              data-testid="cta-get-quote-btn"
            >
              Get Your Free Quote
            </Button>
            <a href="tel:512-563-4371">
              <Button 
                variant="outline"
                className="border-white text-white hover:bg-white hover:text-brand-navy font-body font-medium rounded-full px-8 py-6"
                data-testid="cta-call-btn"
              >
                Call 512-563-4371
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
